import * as z from 'zod/v4';

import { BiChartKind } from '@watershed/constants/bi';
import {
  biExpressionSchema,
  biSemanticMetricExpressionSchema,
} from '@watershed/bi-core';
import { ymIntervalSchema } from '../zodSchemas/utilSchemas';
import { biFilterInputSchema } from './filterSchemas';
import {
  BiCustomMetricRef,
  BiNormalizedMeasure,
  biCustomMetricRefSchema,
  biSavedViewBaseSchema,
  biSavedViewDisplayConfigSchema,
  biSavedViewQueryContextSchema,
} from './types';

/** Enum representing different types of BI V2 query fields (roles) */
export enum BiFieldTypeV2 {
  CustomMetric = 'CustomMetric',
  Metric = 'Metric',
  MetricExpression = 'MetricExpression',
  Dimension = 'Dimension',
  DimensionExpression = 'DimensionExpression',
}

// CustomMetricFieldInput
export const customMetricFieldInputSchema = z.object({
  type: z.literal(BiFieldTypeV2.CustomMetric),
  customMetric: biCustomMetricRefSchema,
});

/** Input type for a custom metric field */
export type CustomMetricFieldInput = z.infer<
  typeof customMetricFieldInputSchema
>;

// MetricFieldInput
export const metricFieldInputSchema = z.object({
  type: z.literal(BiFieldTypeV2.Metric),
  metric: z.object({
    base: biSemanticMetricExpressionSchema,
    denominator: biSemanticMetricExpressionSchema.optional(),
    dropDenominatorFilters: z.boolean().optional(),
    dropDenominatorGrouping: z.boolean().optional(),
  }),
  // Per-metric configuration fields for unit conversion and display
  unit: z.string().optional(),
  decimalPlaces: z.number().int().min(0).max(3).optional(),
});

/** Input type for a metric field */
export type MetricFieldInput = z.infer<typeof metricFieldInputSchema>;

// MetricExpressionFieldInput
export const metricExpressionFieldInputSchema = z.object({
  type: z.literal(BiFieldTypeV2.MetricExpression),
  expression: biExpressionSchema,
  displayName: z.string().nullish(),
});
/** Input type for a metric expression field */
export type MetricExpressionFieldInput = z.infer<
  typeof metricExpressionFieldInputSchema
>;

// DimensionFieldInput
export const dimensionFieldInputSchema = z.object({
  type: z.literal(BiFieldTypeV2.Dimension),
  semanticId: z.string(),
});
/** Input type for a dimension field */
export type DimensionFieldInput = z.infer<typeof dimensionFieldInputSchema>;

// DimensionExpressionFieldInput
export const dimensionExpressionFieldInputSchema = z.object({
  type: z.literal(BiFieldTypeV2.DimensionExpression),
  expression: biExpressionSchema,
  displayName: z.string().nullish(),
});
/** Input type for a dimension expression field */
export type DimensionExpressionFieldInput = z.infer<
  typeof dimensionExpressionFieldInputSchema
>;

// BiFieldInput
export const biFieldInputSchema = z.discriminatedUnion('type', [
  customMetricFieldInputSchema,
  metricFieldInputSchema,
  metricExpressionFieldInputSchema,
  dimensionFieldInputSchema,
  dimensionExpressionFieldInputSchema,
]);

/**
 * Input type for a BI field. Field input types are serializable objects that
 * can be persisted in a saved view, representing the minimal amount of info
 * needed to reconstruct the view later on.
 */
export type BiFieldInput = z.infer<typeof biFieldInputSchema>;

export const biMetricInputSchema = z.discriminatedUnion('type', [
  customMetricFieldInputSchema,
  metricFieldInputSchema,
  metricExpressionFieldInputSchema,
]);

export type BiMetricInput = z.infer<typeof biMetricInputSchema>;

export const biDimensionInputSchema = z.discriminatedUnion('type', [
  dimensionFieldInputSchema,
  dimensionExpressionFieldInputSchema,
]);

export type BiDimensionInput = z.infer<typeof biDimensionInputSchema>;

/**
 * Converts a MetricFieldInput to the older normalized measure type.
 * This should be a temporary way to get a fieldMeta from a MetricFieldInput.
 */
export function normalizedMeasureFromMetricFieldInput(
  input: MetricFieldInput
): BiNormalizedMeasure {
  const normalizedMeasure: BiNormalizedMeasure = {
    baseMeasure: input.metric.base.semanticId,
    baseFilters: [],
  };

  if (input.metric.denominator) {
    normalizedMeasure.normalizingQuery = {
      measure: input.metric.denominator.semanticId,
      ...(input.metric.dropDenominatorGrouping
        ? {
            dimensions: [],
          }
        : {
            computedDimensionType: 'NumeratorIntersection',
          }),
      ...(input.metric.dropDenominatorFilters
        ? {
            filters: [],
          }
        : {
            computedFiltersType: 'NumeratorIntersection',
          }),
    };
  }

  return normalizedMeasure;
}

export function extractBiQueryStateMetrics(metrics: Array<BiMetricInput>): {
  normalizedMeasures: Array<BiNormalizedMeasure>;
  customMetrics: Array<BiCustomMetricRef>;
} {
  const normalizedMeasures = metrics
    .filter(
      (metric): metric is MetricFieldInput =>
        metric.type === BiFieldTypeV2.Metric
    )
    .map((metric) => normalizedMeasureFromMetricFieldInput(metric));

  const customMetrics = metrics
    .filter(
      (metric): metric is CustomMetricFieldInput =>
        metric.type === BiFieldTypeV2.CustomMetric
    )
    .map((metric) => metric.customMetric);

  return {
    normalizedMeasures,
    customMetrics,
  };
}

/**
 * Schema for V2 saved view state.
 */
export const biSavedViewStateV2Schema = z.object({
  chartKind: z.enum(BiChartKind),
  searchTerm: z.string().nullable(),
  queryContext: biSavedViewQueryContextSchema.nullable(),
  displayConfig: biSavedViewDisplayConfigSchema.nullish(),

  /** All metrics and dimensions used in the query */
  metrics: z.array(biMetricInputSchema),
  dimensions: z.array(biDimensionInputSchema),
  /** Filters used in the query **/
  filters: z.array(biFilterInputSchema),
  /**
   * One or more time intervals. Multiple time intervals will result in
   * multiple queries that can be compared.
   */
  timeIntervals: z.array(ymIntervalSchema),
});

export type BiSavedViewStateV2Type = z.TypeOf<typeof biSavedViewStateV2Schema>;

export const SAVED_VIEW_VERSION_V2 = 'bi:2' as const;

/**
 * Schema for V2 saved views (with version and V2-shaped state).
 */
export const biSavedViewV2Schema = biSavedViewBaseSchema.extend({
  version: z.literal(SAVED_VIEW_VERSION_V2),
  state: biSavedViewStateV2Schema,
});

export type BiSavedViewV2Type = z.TypeOf<typeof biSavedViewV2Schema>;
